import { useState, useRef, useEffect, useCallback, useMemo } from "react";

interface ResizeObserverOptions {
  debounceDelay?: number;
  useAnimationFrame?: boolean;
  roundValues?: boolean;
}

interface Size {
  width: number;
  height: number;
}

function useResizeObserver<T extends HTMLElement = HTMLElement>(
    options: ResizeObserverOptions = {}
) {
    const [size, setSize] = useState({ width: 0, height: 0 });
    const ref = useRef<T | null>(null);
    const callbackRef = useRef<((rect: DOMRectReadOnly) => void) | null>(null);
    const timeoutRef = useRef<number | null>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);

    const handleResize = useCallback((rect: DOMRectReadOnly) => {
        const { width, height } = rect;
        const w = options.roundValues ? Math.round(width) : width;
        const h = options.roundValues ? Math.round(height) : height;

        setSize(prev => {
            if (prev.width === w && prev.height === h) return prev;
            return { width: w, height: h };
        });
    }, [options.roundValues]);

    useEffect(() => {
        callbackRef.current = handleResize;
    }, [handleResize]);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const processEntries = (entries: ResizeObserverEntry[]) => {
            if (!ref.current) return;
            const entry = entries[0];
            if (!entry) return;

            const trigger = () => {
                if (callbackRef.current) {
                    callbackRef.current(entry.contentRect);
                }
            };

            if (options.debounceDelay > 0) {
                if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
                timeoutRef.current = window.setTimeout(trigger, options.debounceDelay);
            } else if (options.useAnimationFrame) {
                requestAnimationFrame(trigger);
            } else {
                trigger();
            }
        };

        resizeObserverRef.current = new ResizeObserver(processEntries);
        resizeObserverRef.current.observe(element);

        return () => {
            if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
            if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
        };
    }, [options.debounceDelay, options.useAnimationFrame]);

    return useMemo(() => [ref, size] as const, [size]);
}

export default useResizeObserver;